import { useState } from "react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import Chart from "@/components/charts/Chart"
import DataTable from "@/components/Tables/DataTable"
import { columns } from "./columns"
import { exportReport } from "@/hooks/useReportExport"
import { useAppSelector } from "@/lib/hooks"
import { useReportBranch } from "@/components/Reports/ReportBranchFilter"
import { BrandSelect } from "../../Brand/BrandSelect"
import SalesOrderTypeSelect from "../SalesOrderTypeSelect"

const YearlyReport = ({ orderType, onOrderTypeChange }) => {
  const currentYear = new Date().getFullYear()
  const [yearRange, setYearRange] = useState("last_5_years") // Default selection

  // Calculate date range based on selection
  const getDateRange = () => {
    switch (yearRange) {
      case "current_year":
        return [
          new Date(`${currentYear}-01-01`),
          new Date(`${currentYear}-12-31`),
        ]
      case "last_year":
        return [
          new Date(`${currentYear - 1}-01-01`),
          new Date(`${currentYear - 1}-12-31`),
        ]
      case "last_5_years":
        return [
          new Date(`${currentYear - 4}-01-01`),
          new Date(`${currentYear}-12-31`),
        ]
      case "last_10_years":
        return [
          new Date(`${currentYear - 9}-01-01`),
          new Date(`${currentYear}-12-31`),
        ]
      default:
        return [
          new Date(`${currentYear - 4}-01-01`),
          new Date(`${currentYear}-12-31`),
        ]
    }
  }

  const [startDate, endDate] = getDateRange()

  const selectedBrand = useAppSelector((state) => state.brand.selectedBrandId)
  const { branchId } = useReportBranch()
  const { data, isLoading } = useCustomQuery(
    [
      "yearly-sales-report",
      startDate,
      endDate,
      selectedBrand,
      orderType,
      branchId,
    ],
    `report/sales/yearly?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}&brand_id=${selectedBrand}&order_type=${orderType}&branch_id=${branchId ?? ""}`
  )

  const reversedData = data ? [...data].reverse() : []

  // Prepare chart data
  const chartData = {
    labels: [],
    salesData: [],
    ordersData: [],
  }

  if (data) {
    data.forEach((item) => {
      chartData.labels.push(item.sale_year.toString())
      chartData.salesData.push(item.total_sales)
      chartData.ordersData.push(item.total_orders)
    })
  }

  // export Daily Report
  const handleExport = async () => {
    return exportReport({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      brandId: selectedBrand,
      orderType,
      branchId,
      reportType: "yearly-sales",
    })
  }

  // Calculate summary stats
  const summaryStats = data
    ? {
        totalSales: data.reduce(
          (sum, item) => sum + parseFloat(item.total_sales),
          0
        ),
        totalOrders: data.reduce(
          (sum, item) => sum + parseInt(item.total_orders),
          0
        ),
        averageSales:
          data.reduce((sum, item) => sum + parseFloat(item.total_sales), 0) /
          data.length,
        averageOrders:
          data.reduce((sum, item) => sum + parseInt(item.total_orders), 0) /
          data.length,
        growthRate:
          data.length > 1
            ? ((parseFloat(data[data.length - 1].total_sales) -
                parseFloat(data[0].total_sales)) /
                parseFloat(data[0].total_sales)) *
              100
            : 0,
      }
    : null

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Yearly Sales Report
      </Typography>

      {/* Year Range Selector Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select Year Range
          </Typography>
          <div className="flex flex-wrap items-center gap-2">
            <FormControl className="flex-1">
              <InputLabel>Year Range</InputLabel>
              <Select
                value={yearRange}
                onChange={(e) => setYearRange(e.target.value)}
                label="Year Range"
              >
                <MenuItem value="current_year">Current Year</MenuItem>
                <MenuItem value="last_year">Last Year</MenuItem>
                <MenuItem value="last_5_years">Last 5 Years</MenuItem>
                <MenuItem value="last_10_years">Last 10 Years</MenuItem>
              </Select>
            </FormControl>
            <SalesOrderTypeSelect
              value={orderType}
              onChange={onOrderTypeChange}
            />
            <div className="flex-1">
              <BrandSelect />
            </div>
            <Button
              variant="outlined"
              style={{ marginTop: "10px" }}
              onClick={() => handleExport()}
              sx={{
                textTransform: "capitalize",
                borderRadius: "7px",
                fontWeight: "500",
                fontSize: "13px",
                padding: "6px 3px",
              }}
              color="primary"
            >
              Export
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {summaryStats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Total Sales</Typography>
              <Typography variant="h5" color="primary">
                Rs.{" "}
                {summaryStats.totalSales.toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Total Orders</Typography>
              <Typography variant="h5" color="secondary">
                {summaryStats.totalOrders.toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Avg. Yearly Sales</Typography>
              <Typography variant="h5">
                Rs.{" "}
                {summaryStats.averageSales.toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Paper>
          </Grid>
          {/* <Grid item xs={12} md={3}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">
                {data && data.length > 1
                  ? `${data[data.length - 1].sale_year} Growth`
                  : "Growth Rate"}
              </Typography>
              <Typography
                variant="h5"
                color={
                  summaryStats.growthRate >= 0 ? "success.main" : "error.main"
                }
              >
                {summaryStats.growthRate.toFixed(2)}%
              </Typography>
            </Paper>
          </Grid> */}
        </Grid>
      )}

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Yearly Sales
              </Typography>
              <Chart
                type="line"
                data={chartData.salesData}
                labels={chartData.labels}
                title="Yearly Sales"
                backgroundColor="rgba(63, 81, 181, 0.5)"
                borderColor="#3f51b5"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Yearly Orders
              </Typography>
              <Chart
                type="bar"
                data={chartData.ordersData}
                labels={chartData.labels}
                title="Yearly Orders"
                backgroundColor="rgba(76, 175, 80, 0.5)"
                borderColor="#4caf50"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Detailed Yearly Data
          </Typography>
          <DataTable
            columns={columns}
            data={reversedData}
            isLoading={isLoading}
            sx={{ mt: 2 }}
          />
        </CardContent>
      </Card>
    </Box>
  )
}

export default YearlyReport
